import { ref, computed, watchEffect, watch, onMounted, onUnmounted } from 'vue';
import { isEmpty, isEqual } from 'lodash';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import UploadIcon from 'components/icons/upload-icon/index.vue';
import AnimationProcessing from 'components/animations/processing';
import { useField } from 'vee-validate';
import i18n from 'src/assets/i18n/i18n';
import { createImagePreSignedURL } from 'src/utils/helper/misc';
import { usePolling } from 'src/composables/usePolling';

export default {
  components: {
    UploadIcon,
    AnimationProcessing
  },

  props: {
    errors: {
      type: Object,
      default: {}
    },
    maxSize: {
      type: Number,
      default: 8388608
    },
    min: {
      type: Number,
      default: 3
    },
    max: {
      type: Number,
      default: 15
    },
    priority: {
      type: Number,
      default: 1
    },
    size: {
      type: Number,
      default: 2
    },
    format: {
      type: String,
      default: 'png, jpg, jpeg'
    },
    rules: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    dragText: {
      type: String,
      default: ''
    }
  },

  setup(props) {
    const store = useStore();
    const route = useRoute();
    const $t = i18n().global.t;
    const minWidth = process.env.MIN_IMAGE_WIDTH;
    const minHeight = process.env.MIN_IMAGE_HEIGHT;

    const { errors: imageErrors, validate: imageValidators, value: imageValue } = useField(props.name, props.rules);
    const { start: startPolling, stop: stopPolling, isPolling } = usePolling(avStatusPollingFunction, 5000);

    const loading = ref(false);
    const sortIndex = ref(1);
    const bundleImageFlag = ref(1);
    const currentPollingImages = ref(null);
    const currentPollingPriority = ref(null);
    const errorMessage = ref('');

    const images = computed(() =>
      isEqual(bundleImageFlag.value, props.priority)
        ? store.getters['bundle/getBundleInfo']?.assets?.main
        : store.getters['bundle/getBundleInfo']?.assets?.inspirational
    );
    const bundleMainImageScanStatus = computed(() => store.getters['bundle/getBundleMainImageScanStatus']);
    const bundleInspirationImageScanStatus = computed(
      () => store.getters['bundle/getBundleInspirationImageScanStatus']
    );

    /**
     * Handle file upload validation and if valid file, save the upload file.
     * @param {Object} event
     */
    const files = ref([]);

    watchEffect(() => {
      if (!isEmpty(images.value)) {
        imageValue.value = images.value;
        imageValidators();
      }
    });

    watch(
      () => images.value,
      async (newVal, oldVal) => {
        const extractMd5 = newVal?.map(file => ({
          md5: file?.md5Checksum,
          imageType: file?.imageType
        }));
        store.commit('bundle/setAlreadyUploadedMd5', extractMd5);
        if (!isEmpty(oldVal) && isEmpty(newVal)) {
          imageValue.value = images.value;
          imageValidators();
        }
      }
    );

    // Helper function to validate file type
    function validateFileType(file) {
      if (!file.type.startsWith('image/')) {
        return $t('pages.bundle.errorMessages.only_png_jpg_error');
      }

      const allowedFormats = props.format.split(',').map(format => format.trim());
      const isFileFormatInvalid = !allowedFormats.some(format => file.type.includes(format));
      if (isFileFormatInvalid) {
        return $t('pages.bundle.errorMessages.only_png_jpg_error');
      }

      return null;
    }

    // Helper function to validate file size
    function validateFileSize(file) {
      const ifFileSizeInvalid = file.size > props.maxSize;
      if (ifFileSizeInvalid) {
        return $t('pages.bundle.errorMessages.image_size', { fileName: file.name });
      }
      return null;
    }

    // Helper function to validate image resolution
    function validateImageResolution(img) {
      const isFileResolutionInValid =
        !isEqual(img.width, 2 * img.height) || img.width < minWidth || img.height < minHeight;
      if (isFileResolutionInValid) {
        return $t('pages.bundle.errorMessages.image_ratio', {
          minWidth: minWidth,
          minHeight: minHeight
        });
      }
      return null;
    }

    // Helper function to process image and validate it
    function processImageFile(file, reader) {
      return new Promise(resolve => {
        reader.onload = e => {
          const img = new Image();
          img.src = e.target.result;
          img.onload = () => {
            const resolutionError = validateImageResolution(img);
            if (resolutionError) {
              showError(files, file, resolutionError);
              resolve(null);
              return;
            }

            resolve({ file: file });
          };
        };
      });
    }

    // Helper function to validate and process a single file
    async function validateAndProcessFile(file) {
      // Basic file type validation
      const typeError = validateFileType(file);
      if (typeError) {
        showError(files, file, typeError);
        return null;
      }

      // File size validation
      const sizeError = validateFileSize(file);
      if (sizeError) {
        showError(files, file, sizeError);
        return null;
      }

      // Process image and validate resolution
      const reader = new FileReader();
      reader.readAsDataURL(file);
      return await processImageFile(file, reader);
    }

    // Helper function to handle accepted files upload
    function handleAcceptedFiles(acceptedFilesArray) {
      const errorMsz = props.priority === 1
        ? $t('pages.bundle.errorMessages.max_img')
        : $t('pages.bundle.errorMessages.max_insp_img');

      if (acceptedFilesArray.length > 15) {
        store.commit('errors/add', {
          response: {
            status: 500,
            data: {
              message: errorMsz
            }
          }
        });
        return;
      }

      if (acceptedFilesArray.length > 0) {
        const alreadyUploadedMd5 = store.getters['bundle/getAlreadyUploadedMd5'];
        loading.value = true;
        createImagePreSignedURL(acceptedFilesArray, alreadyUploadedMd5, 'bundle', false, props.priority)
          .then(res => {
            if (res?.length) {
              uploadImage(res);
            }
          })
          .catch(error => {
            loading.value = false;
            throw error;
          });
      }
    }

    async function upload(event) {
      const filesArray = Array.from(event.target.files);
      const acceptedFilesArray = [];

      const apiPromises = filesArray.map(async file => {
        try {
          const result = await validateAndProcessFile(file);
          if (result) {
            acceptedFilesArray.push(result);
          }
        } catch {
          loading.value = false;
          files.value = [];
        }
      });

      await Promise.allSettled(apiPromises);
      handleAcceptedFiles(acceptedFilesArray);
      files.value = [];
    }

    /**
     * Do not upload the file if invalid.
     * Show notification error.
     * @param {Array} files
     * @param {Object} file
     * @param {String} message
     */
    const showError = (files, file, message) => {
      errorMessage.value = message;
      files.value = files.value.filter(image => !isEqual(image.name, file.name));
      store.commit('errors/add', {
        response: {
          status: 500,
          data: {
            message: errorMessage.value
          }
        }
      });
    };

    /**
     *Fetch api to save the uploaded file.
     * @param {Object} file
     */
    const uploadImage = async fileArray => {
      loading.value = true;

      if (props.priority === 1) {
        store.commit('bundle/setBundleMainImageScanStatus', '');
      } else {
        store.commit('bundle/setBundleInspirationImageScanStatus', '');
      }

      let assets = [];
      fileArray?.forEach(img => {
        assets.push({
          // fileName: route.params.id + '_' + img.md5 + '.' + img?.file?.type?.split('/')[1],
          fileName: img?.file?.name,
          fileType: img?.file?.type,
          fileSize: img?.file?.size,
          imageType: props.priority === 1 ? 'main' : 'inspirational',
          md5Checksum: img.md5,
          sortOrder: sortIndex.value++,
          priority: props.priority
        });
      });
      const payload = { bundleId: route.params.id, assets };
      return store
        .dispatch('bundle/uploadBundleImage', payload)
        .then(async response => {
          if (response?.assets) {
            await checkBundleAvStatus(response?.assets);
          }
          imageValidators();
        })
        .catch(error => {
          loading.value = false;
          throw error;
        });
    };

    // Polling function for AV status checking
    async function avStatusPollingFunction(signal) {
      if (!currentPollingImages.value || !currentPollingPriority.value) {
        return;
      }

      try {
        const response = await store.dispatch('bundle/getBundleImageAvStatus', route?.params?.id);

        if (signal.aborted) return;

        if (response?.assets) {
          const bundleImageData = response?.assets;
          const hasEmptyStatus = bundleImageData.some(image => image?.avStatus === '');

          if (hasEmptyStatus) {
            // Continue polling - status still empty
            return;
          }

          // All images have status, stop polling and process results
          updateScanStatus(bundleImageData);
          updateBundleImageState(bundleImageData, currentPollingImages.value);
          stopPollingAndReset();
          return;
        } else {
          // No response, stop polling
          loading.value = false;
          stopPollingAndReset();
          return;
        }
      } catch (error) {
        if (!signal.aborted) {
          loading.value = false;
          console.error('Error checking AV status:', error);
          stopPollingAndReset();
        }
        return;
      }
    }

    function stopPollingAndReset() {
      stopPolling();
      // Clear polling state
      currentPollingImages.value = null;
      currentPollingPriority.value = null;
    }

    /**
     * API to check the AvStatus of uploaded file.
     */
    const checkBundleAvStatus = async uploadedImages => {
      // Stop any existing polling
      stopPolling();

      loading.value = true;
      currentPollingImages.value = uploadedImages;
      currentPollingPriority.value = props.priority;

      // Set initial scan status
      if (props.priority === 1) {
        store.commit('bundle/setBundleMainImageScanStatus', '');
      } else {
        store.commit('bundle/setBundleInspirationImageScanStatus', '');
      }

      // Start polling for AV status
      startPolling();
    };

    const updateScanStatus = images => {
      const mainFilesWithFalseAvStatus = [];
      const inspirationFilesWithFalseAvStatus = [];

      images.forEach(item => {
        if (item.avStatus === 'false') {
          let _priority;
          if (item?.imageType === 'main') {
            mainFilesWithFalseAvStatus.push(item.fileName);
            _priority = 1;
          } else {
            inspirationFilesWithFalseAvStatus.push(item.fileName);
            _priority = 2;
          }
          store.dispatch('bundle/deleteBundleImage', {
            payload: { bundleId: route?.params?.id, imageId: item?.id, md5: item?.md5Checksum },
            priority: _priority
          });
        }
      });

      if (bundleMainImageScanStatus.value == '') {
        updateMainImageScanStatus(mainFilesWithFalseAvStatus);
      }

      if (bundleInspirationImageScanStatus.value == '') {
        updateInspirationImageScanStatus(inspirationFilesWithFalseAvStatus);
      }
    };

    const updateMainImageScanStatus = mainFilesWithFalseAvStatus => {
      if (mainFilesWithFalseAvStatus?.length) {
        store.commit('bundle/setBundleMainImageScanStatus', 'false');
        store.commit('bundle/setBundleMainImagesWithFalseAvCheck', mainFilesWithFalseAvStatus);
      } else {
        store.commit('bundle/setBundleMainImageScanStatus', 'true');
      }
    };

    const updateInspirationImageScanStatus = inspirationFilesWithFalseAvStatus => {
      if (inspirationFilesWithFalseAvStatus?.length) {
        store.commit('bundle/setBundleInspirationImageScanStatus', 'false');
        store.commit('bundle/setBundleInpirationImagesWithFalseAvCheck', inspirationFilesWithFalseAvStatus);
      } else {
        store.commit('bundle/setBundleInspirationImageScanStatus', 'true');
      }
    };

    const updateBundleImageState = (bundleImageData, uploadedImages) => {
      const newBundleResponse = [];
      bundleImageData?.forEach(image => {
        uploadedImages?.forEach(resImage => {
          if (image?.md5Checksum === resImage?.md5Checksum && image?.avStatus !== '') {
            newBundleResponse.push(image);
          }
        });
      });
      if (isEqual(bundleImageFlag.value, props.priority)) {
        store.commit('bundle/UPDATE_BUNDLE_IMAGE', newBundleResponse);
      } else {
        store.commit('bundle/UPDATE_INSPIRATIONAL_IMAGE', newBundleResponse);
      }
      loading.value = false;
    };

    onMounted(async () => {
      if (!isEmpty(images.value) && images.value?.length) {
        const bundleImages = images.value;

        const extractMd5 = bundleImages?.map(file => ({
          md5: file?.md5Checksum,
          imageType: file?.imageType
        }));
        store.commit('bundle/setAlreadyUploadedMd5', extractMd5);

        if (bundleImages.every(item => item.avStatus === 'true')) {
          if (bundleImages?.[0]?.imageType === 'main') {
            updateMainImageScanStatus([]);
          } else if (bundleImages?.[0]?.imageType === 'inspirational') {
            updateInspirationImageScanStatus([]);
          }
        }

        if (bundleImages.some(item => item.avStatus === '') || bundleImages.some(item => item.avStatus === 'false')) {
          if (bundleImages?.[0]?.imageType === 'main') {
            store.commit('bundle/REMOVE_ALL_BUNDLE_IMAGES');
          } else if (bundleImages?.[0]?.imageType === 'inspirational') {
            store.commit('bundle/REMOVE_ALL_INSPIRATION_BUNDLE_IMAGES');
          }
          checkBundleAvStatus(bundleImages);
        }
      }
    });

    // Cleanup polling on component unmount
    onUnmounted(() => {
      stopPolling();
    });

    return {
      props,
      minWidth,
      minHeight,
      errorMessage,
      files,
      images,
      imageErrors,
      bundleImageFlag,
      loading,
      uploadImage,
      showError,
      upload,
      checkBundleAvStatus,
      updateBundleImageState,
      updateScanStatus,
      updateInspirationImageScanStatus,
      updateMainImageScanStatus,
      isPolling,
      stopPolling
    };
  }
};
